import React from "react"
import type { ReactElement } from "react"
import { useState } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Alert,
} from "@mui/material"
import { Person as PersonIcon, Save as SaveIcon, Close as CloseIcon } from "@mui/icons-material"
import { createContactForOwner } from "../../../services/documentsService"

interface CreateContactModalProps {
  open: boolean
  onClose: () => void
  onContactCreated: (contact: any) => void
  initialPhone?: string
}

interface ContactFormData {
  fullName: string
  phone: string
  email: string
  address: string
}

const CreateContactModal: React.FC<CreateContactModalProps> = ({
  open,
  onClose,
  onContactCreated,
  initialPhone = "",
}): ReactElement => {
  const [formData, setFormData] = useState<ContactFormData>({
    fullName: "",
    phone: initialPhone,
    email: "",
    address: "",
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  React.useEffect(() => {
    if (open) {
      setFormData({
        fullName: "",
        phone: initialPhone,
        email: "",
        address: "",
      })
      setError(null)
    }
  }, [open, initialPhone])

  const handleInputChange = (field: keyof ContactFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
    if (error) setError(null)
  }

  const validateForm = (): boolean => {
    if (!formData.fullName.trim()) {
      setError("El nombre completo es requerido")
      return false
    }
    if (!formData.phone.trim()) {
      setError("El número de teléfono es requerido")
      return false
    }

    const phoneRegex = /^[0-9]{8,15}$/
    if (!phoneRegex.test(formData.phone.trim())) {
      setError("Por favor, digita un número de teléfono válido (8-15 dígitos).")
      return false
    }

    if (!formData.email.trim()) {
      setError("El email es requerido")
      return false
    }
    if (!formData.address.trim()) {
      setError("La dirección es requerida")
      return false
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setError("El formato del email no es válido")
      return false
    }

    return true
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    setLoading(true)
    setError(null)

    try {
      const formDataToSend = new FormData()
      formDataToSend.append("FullName", formData.fullName)
      formDataToSend.append("Phone", formData.phone)
      formDataToSend.append("Email", formData.email)
      formDataToSend.append("Address", formData.address)

      const response = await createContactForOwner(formDataToSend)

      if (response && response.status === 200) {
        let contactId = null

        if (response.data && typeof response.data === "object" && "contactId" in response.data) {
          contactId = response.data.contactId
        } else if (response.data && typeof response.data === "object" && "id" in response.data) {
          contactId = response.data.id
        } else {
          contactId = Date.now()
        }

        const newContact = {
          id: contactId,
          fullName: formData.fullName,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
        }

        onContactCreated(newContact)
        onClose()
      } else {
        setError("Error al crear el contacto")
      }
    } catch (error: any) {
      console.error("Error al crear contacto:", error)

      if (error.response) {
        console.error("Respuesta de error:", error.response.data)
        setError(`Error al crear el contacto: ${error.response.data || error.message}`)
      } else {
        setError("Error al crear el contacto. Inténtalo de nuevo.")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <PersonIcon />
        Crear Nuevo Contacto
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Nombre Completo"
                value={formData.fullName}
                onChange={(e) => handleInputChange("fullName", e.target.value)}
                fullWidth
                required
                disabled={loading}
                placeholder="Ej: Juan Pérez García"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Teléfono"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                fullWidth
                required
                disabled={loading}
                placeholder="Ej: +34 123 456 789"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                fullWidth
                required
                disabled={loading}
                placeholder="Ej: juan@email.com"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Dirección"
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                fullWidth
                required
                disabled={loading}
                multiline
                rows={2}
                placeholder="Ej: Calle Mayor 123, Madrid"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading} startIcon={<CloseIcon />}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
        >
          {loading ? "Creando..." : "Crear Contacto"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateContactModal
